import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import PageHelmet from "../component/common/Helmet";
import HeaderAbs from "../component/header/HeaderAbs";
import FooterTwo from "../component/footer/FooterTwo";
import mermaid from "mermaid";

// Initialize Mermaid once globally with security settings
let mermaidInitialized = false;

const MermaidDiagram = ({ title, chart }) => {
    const [svg, setSvg] = useState(null);
    const [renderError, setRenderError] = useState(null);
    const renderIdRef = useRef(
        `mermaid-${Math.random().toString(36).slice(2)}-${Date.now()}`
    );

    useEffect(() => {
        // Skip Mermaid during prerender (react-snap)
        if (typeof window === "undefined") {
            setRenderError(new Error("Server-side render"));
            return;
        }

        const isPrerender =
            window.__REACT_SNAP__ ||
            (typeof navigator !== "undefined" &&
                typeof navigator.userAgent === "string" &&
                navigator.userAgent.includes("ReactSnap"));

        if (isPrerender) {
            setRenderError(new Error("Prerender mode"));
            return;
        }

        let cancelled = false;
        setSvg(null);
        setRenderError(null);

        // Initialize Mermaid with strict security settings (once)
        if (!mermaidInitialized) {
            mermaid.initialize({
                startOnLoad: false,
                securityLevel: "strict",
                theme: "neutral",
                flowchart: { htmlLabels: false },
            });
            mermaidInitialized = true;
        }

        const renderId = renderIdRef.current;

        // Render the diagram
        mermaid
            .render(renderId, chart)
            .then((result) => {
                if (!cancelled && result && result.svg) {
                    setSvg(result.svg);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    console.error("Mermaid render error:", err);
                    setRenderError(err);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [chart]);

    return (
        <div className="inner mb--50">
            <h3 className="title">{title}</h3>
            <div className="case-study-diagram">
                {svg ? (
                    <div
                        className="case-study-diagram__frame"
                        aria-label={title}
                        dangerouslySetInnerHTML={{ __html: svg }}
                    />
                ) : (
                    <div className="case-study-diagram__frame">
                        <p className="description mb--10">
                            Diagram preview{" "}
                            {renderError ? "(unavailable in this environment)" : "(loading...)"}.
                            Mermaid source is shown below.
                        </p>
                        <pre>
                            <code>{chart}</code>
                        </pre>
                    </div>
                )}
            </div>
        </div>
    );
};

const DecisionsTable = ({ rows }) => {
    return (
        <div className="inner mb--50">
            <h3 className="title">Key Decisions</h3>
            <div className="rn-decisions-table-wrapper">
                <table className="rn-decisions-table">
                    <thead>
                        <tr>
                            <th>Decision</th>
                            <th>Options</th>
                            <th>Why this</th>
                            <th>Tradeoff</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.decision}>
                                <td>
                                    <strong>{row.decision}</strong>
                                </td>
                                <td>{row.options}</td>
                                <td>{row.why}</td>
                                <td>{row.tradeoff}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const FailureModes = ({ items }) => {
    return (
        <div className="inner mb--50">
            <h3 className="title">Failure Modes &amp; Mitigations</h3>
            <ul className="case-study-pairs">
                {items.map((item) => (
                    <li key={item.failure}>
                        <strong>{item.failure}</strong>
                        <br />
                        {item.mitigation}
                    </li>
                ))}
            </ul>
        </div>
    );
};

const CaseStudyHero = ({ title, valueStatement, tldr }) => {
    return (
        <div className="blog-single-page-title text-center pt--60">
            <h2 className="title theme-gradient">{title}</h2>
            <p className="description case-study-value">{valueStatement}</p>

            <div className="case-study-tldr">
                <h4 className="title">TL;DR</h4>
                <ul>
                    {tldr.map((item) => (
                        <li key={item.label}>
                            <strong>{item.label}:</strong> {item.text}
                        </li>
                    ))}
                </ul>
            </div>

            <p className="mt--20">
                <Link to="/reference-architectures">← Reference Architectures</Link>
            </p>
        </div>
    );
};

const CTAButtons = ({ items }) => (
    <div className="inner">
        <div
            className="d-flex justify-content-center flex-wrap"
            style={{ gap: "12px" }}
        >
            {items.map((cta) =>
                cta.to ? (
                    <Link key={cta.label} className="rn-button-style--2 btn-solid" to={cta.to}>
                        {cta.label}
                    </Link>
                ) : (
                    <a
                        key={cta.label}
                        className="rn-button-style--2 btn-solid"
                        href={cta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {cta.label}
                    </a>
                )
            )}
        </div>
    </div>
);

const CASE_STUDIES = {
    "enterprise-rag-platform": {
        title: "Enterprise RAG Platform (Reference Architecture)",
        valueStatement:
            "Secure, permission-aware retrieval that grounds LLM responses with citations—built with ingestion, hybrid retrieval, model gateway controls, and end-to-end observability.",
        ogImage: "/assets/images/proof/rag-sequence.png",
        about: ["RAG", "Hybrid retrieval", "RBAC/ABAC", "Evals", "Observability"],
        tldr: [
            { label: "Delivers", text: "fast answers over fragmented runbooks/KBs with citations" },
            { label: "Controls", text: "tenant isolation + RBAC/ABAC + audit trail" },
            { label: "Operates", text: "quality + latency + cost telemetry; eval gates prevent regressions" },
            { label: "Reusable", text: "ingestion → retrieval → evaluation harness → dashboards" },
        ],
        where: {
            useWhen: [
                "Knowledge is distributed across sources and access is permissioned.",
                "You need citations, auditability, and predictable cost/latency.",
            ],
            dontUseWhen: [
                "The task is purely transactional (prefer deterministic APIs).",
                "You need full document reasoning without grounding (prefer doc workflows first).",
            ],
        },
        diagram: {
            title: "Architecture diagram (high level)",
            mermaid: `flowchart LR
  subgraph Sources[Knowledge Sources]
    A[Runbooks / KB]
    B[Docs / PDFs]
    C[Tickets / Notes]
  end

  subgraph Ingest[Ingestion + Indexing]
    D[Chunking + Metadata]
    E[ACL/Policy Tags]
    F[Embeddings]
    G[(Vector Index\\nTenant namespaces)]
    H[(Keyword Index\\nBM25)]
    V[Versioning + Freshness]
  end

  subgraph Query[Query-time Retrieval]
    Q[User Query]
    P[AuthZ Check\\nRBAC/ABAC]
    R[Hybrid Retrieve\\nDense+Sparse]
    RR[Reranker]
    K[Context Builder\\nWindow + dedupe]
    CCH[(Cache)]
  end

  subgraph Gen[Model Gateway + Response]
    MG[Model Gateway\\nrate limits, routing]
    GR[Guardrails\\nPII/safety checks]
    OUT[Answer + Citations]
  end

  subgraph Obs[Observability + Evals]
    T[Traces/Logs\\nretrieval→gen]
    M[Metrics\\nquality/latency/cost]
    EV[Eval Suite\\noffline+online]
    FB[Feedback Store]
  end

  A-->D; B-->D; C-->D
  D-->E-->F-->G
  D-->H
  D-->V
  Q-->P-->R
  R-->RR-->K-->MG-->GR-->OUT
  K<-->CCH
  R-->T; MG-->T; OUT-->FB
  T-->M-->EV
`,
        },
        permissionModel: [
            "Documents carry tenant + ACL attributes at ingest (account, role, tags).",
            "Query-time retrieval enforces deny-by-default: filters apply before retrieval and again before context assembly.",
            "Citations include doc ID + section anchors for auditability and review.",
        ],
        decisions: [
            {
                decision: "Hybrid retrieval (BM25 + embeddings)",
                options: "BM25-only / dense-only / hybrid",
                why: "Improves recall on jargon, acronyms, and exact-match queries.",
                tradeoff: "More tuning and infrastructure surface area.",
            },
            {
                decision: "High top-k + reranking",
                options: "No reranker / cross-encoder rerank / LLM rerank",
                why: "Reduces missed chunks and improves relevance under noisy corpora.",
                tradeoff: "Extra latency/cost (managed via caching and budgets).",
            },
            {
                decision: "Central model gateway",
                options: "Direct SDK calls / gateway layer",
                why: "Standardizes rate limits, routing, prompt/version control, and fallbacks.",
                tradeoff: "Adds an integration point to operate and secure.",
            },
            {
                decision: "Evals + telemetry as release gates",
                options: "Ad hoc testing / CI eval thresholds / canary-only",
                why: "Prevents silent regressions as prompts/models/indexes evolve.",
                tradeoff: "Requires maintaining datasets, rubrics, and dashboards.",
            },
        ],
        failureModes: [
            {
                failure: "Relevant chunk not retrieved",
                mitigation: "Hybrid retrieval + higher top-k + reranking; targeted query rewriting for known patterns.",
            },
            {
                failure: "Wrong or stale chunk used",
                mitigation: "Versioning + freshness signals; cache invalidation rules; cite-only response patterns when confidence is low.",
            },
            {
                failure: "Hallucinated synthesis",
                mitigation: "Grounding checks; citations required for claims; refusal patterns for low-evidence responses.",
            },
            {
                failure: "Prompt/model regression",
                mitigation: "Eval gating + replay harness; rollback strategy tied to quality/latency/cost signals.",
            },
        ],
        evaluation: [
            "Offline golden sets + adversarial cases (CI gate).",
            "Online metrics: task success, time-to-answer, escalation, and cost/latency telemetry.",
            "Canary rollout with rollback triggers tied to measured signals.",
        ],
        evidence: {
            artifacts: [
                "Visual: rag-sequence.png — end-to-end request path with policy filter, top-k, reranker, and cache callouts.",
                "Output: rag-trace.json — retrieval trace + citations (sanitized).",
            ],
            visual: {
                src: "/assets/images/proof/rag-sequence.png",
                alt: "RAG request path sequence diagram",
                caption:
                    "Synthetic request path showing AuthZ filtering, hybrid retrieval, reranking, context assembly, and cite-required responses.",
            },
            downloads: [
                { label: "rag-trace.json", href: "/assets/artifacts/rag-trace.json" },
                { label: "rag-architecture.svg", href: "/assets/images/proof/rag-architecture.svg" },
            ],
            sample: {
                title: "rag-trace.json (sanitized)",
                language: "json",
                content: `{
  "request_id": "req_123",
  "tenant_id": "tenant_acme",
  "user_role": "support_engineer",
  "query": "How do we rotate the on-call token safely?",
  "filters_applied": {
    "mode": "deny_by_default",
    "rbac_roles": ["support_engineer"],
    "abac_tags": ["team:oncall", "env:prod"]
  },
  "retrieval": {
    "strategy": "hybrid",
    "top_k": 24,
    "reranked_hits": [
      { "doc_id": "runbook-oncall-rotation", "chunk_id": "chunk_07", "score": 0.94 }
    ]
  },
  "citations": [{ "doc_id": "runbook-oncall-rotation", "section_anchor": "#token-rotation" }]
}`,
            },
        },
        ctas: [
            { label: "View AWS Marketplace Deployments", to: "/enterprise-ai#marketplace" },
            { label: "See Evals-as-CI", to: "/case-studies/evals-as-ci-aiops" },
        ],
    },
    "agent-orchestration-governance": {
        title: "Tool-Calling Agents with Governance",
        valueStatement:
            "Agent runtime patterns for regulated workflows: contract-first tools, policy enforcement, HITL approvals, and auditable traces.",
        ogImage: "/assets/images/proof/agent-approval-boundary.png",
        about: ["Agents", "Tool contracts", "Governance", "HITL approvals", "Audit logs"],
        tldr: [
            { label: "Delivers", text: "safe workflow automation across enterprise systems" },
            { label: "Controls", text: "tool allowlists + schema validation + least privilege" },
            { label: "Governs", text: "approvals for high-impact actions + full audit trail" },
            { label: "Operates", text: "traces + evals for tool accuracy and policy compliance" },
        ],
        diagram: {
            title: "Architecture diagram (agent runtime)",
            mermaid: `flowchart LR
  U[User]-->A[Agent Runtime]

  subgraph Tools[Tool Layer]
    TR[Tool Registry\\nschemas + least privilege]
    TS[Tool Sandbox/Adapters]
  end

  subgraph Policy[Policy + Governance]
    PE[Policy Engine\\nallowlist, rate, content]
    HITL[Approval Queue\\nreason codes]
    AUD[Audit Log\\nwho/what/when/why]
  end

  subgraph State[State + Data]
    SS[(State Store TTL)]
    RS[(Redaction Store)]
  end

  subgraph Obs[Observability + Evals]
    OT[Traces/Metrics]
    EV[Tool-use evals\\naccuracy/violations]
  end

  A-->PE
  A-->SS
  A-->TR-->TS
  PE-->HITL
  TS-->AUD
  A-->OT-->EV
  HITL-->A
  RS-->SS
`,
        },
        toolContracts: [
            "Every tool has an input/output schema (JSON Schema).",
            "Every tool is least-privilege (explicit permissions per tool).",
            "Idempotency keys are supported to prevent duplicate actions on retries.",
            "Tools are invoked only through the registry (no freeform calls).",
        ],
        approvalMatrix: [
            "Payments / transfers",
            "Permission changes",
            "External communications",
            "Destructive actions (delete/disable)",
            "Any action above a configurable risk threshold",
        ],
        decisions: [
            {
                decision: "Contract-first tool registry",
                options: "Freeform tool calls / schema-defined tools",
                why: "Improves reliability and enables validation before execution.",
                tradeoff: "More upfront tool definition work.",
            },
            {
                decision: "Policy engine before execution",
                options: "Post-hoc review / pre-execution enforcement",
                why: "Prevents unsafe actions and standardizes governance controls.",
                tradeoff: "Risk of overblocking without good reason codes/tuning.",
            },
            {
                decision: "HITL approvals for high-risk actions",
                options: "Fully automated / always-human / risk-tiered HITL",
                why: "Scales automation while keeping trust and control boundaries.",
                tradeoff: "Adds friction for some workflows; requires good UX.",
            },
            {
                decision: "Audit log as source of truth",
                options: "Best-effort logs / structured audit events",
                why: "Enables incident response, compliance review, and debugging.",
                tradeoff: "Requires consistent event schemas and retention policy.",
            },
        ],
        failureModes: [
            {
                failure: "Wrong tool selected or unsafe tool invoked",
                mitigation: "Tool allowlists + policy checks + tool-use evals; block unknown tools by default.",
            },
            {
                failure: "Invalid tool arguments / hallucinated fields",
                mitigation: "Schema validation + argument repair loop; reject and request clarification when needed.",
            },
            {
                failure: "Duplicate actions due to retries/timeouts",
                mitigation: "Idempotency keys + state store; safe retry semantics and timeouts.",
            },
            {
                failure: "Approval queue becomes a bottleneck",
                mitigation: "Risk tiers + batching; clear reason codes; tune policies to reduce unnecessary escalations.",
            },
        ],
        evaluation: [
            "Tool selection accuracy and schema compliance rate.",
            "Policy violation rate and escalation/approval rate by risk tier.",
            "Operational signals: retries, timeouts, rollback/incident frequency.",
        ],
        evidence: {
            artifacts: [
                "Visual: agent-approval-boundary.png — approval boundary between read-only and high-impact tools.",
                "Output: agent-audit-log.json — audit record with policy decision + reason code + approval metadata (sanitized).",
            ],
            visual: {
                src: "/assets/images/proof/agent-approval-boundary.png",
                alt: "Agent approval boundary diagram",
                caption:
                    "Synthetic boundary showing contract-first tools, policy enforcement, HITL approvals for high-impact actions, and audit logging.",
            },
            downloads: [
                { label: "agent-audit-log.json", href: "/assets/artifacts/agent-audit-log.json" },
                { label: "agent-orchestration.svg", href: "/assets/images/proof/agent-orchestration.svg" },
            ],
            sample: {
                title: "agent-audit-log.json (sanitized)",
                language: "json",
                content: `{
  "conversation_id": "conv_456",
  "step_id": "step_03",
  "actor": { "user_id": "u_operator_1", "role": "operator" },
  "tool_name": "iam.change_role_policy",
  "policy_decision": "HITL_REQUIRED",
  "reason_code": "risk_tier_high",
  "approval_required": true,
  "approver": "u_approver_2",
  "trace_id": "trace_789"
}`,
            },
        },
        ctas: [
            { label: "See Evals-as-CI", to: "/case-studies/evals-as-ci-aiops" },
            { label: "See Enterprise RAG", to: "/case-studies/enterprise-rag-platform" },
        ],
    },
    "evals-as-ci-aiops": {
        title: "Evals-as-CI for LLM Apps",
        valueStatement:
            "Ship AI changes safely with eval gates, canary releases, telemetry, drift detection, and rollback triggers.",
        ogImage: "/assets/images/proof/eval-gate-report.png",
        about: ["LLMOps", "Evaluation", "Release engineering", "Canary rollouts", "Rollback"],
        tldr: [
            { label: "Prevents", text: "silent regressions in prompts/models/tools" },
            { label: "Ships safely", text: "CI eval thresholds + canary + rollback" },
            { label: "Measures", text: "quality, policy violations, latency, and cost" },
            { label: "Standardizes", text: "templates so teams onboard fast" },
        ],
        diagram: {
            title: "Pipeline diagram (Evals-as-CI)",
            mermaid: `flowchart LR
  PR[PR / Change]-->CI[CI Pipeline]
  CI-->EV[Offline Eval Suite\\nGolden + Adversarial]
  EV-->G{Pass thresholds?}
  G--No-->FAIL[Block merge\\nReport diffs]
  G--Yes-->REL[Release Candidate]
  REL-->CAN[Canary % Traffic]
  CAN-->MON[Online Monitoring\\nquality/latency/cost]
  MON-->RB{Rollback triggers?}
  RB--Yes-->ROLL[Rollback + incident]
  RB--No-->GA[Full rollout]
  MON-->DASH[Dashboards + Alerts]
`,
        },
        thresholds: [
            "Build fails if tool-use schema compliance < 99%.",
            "Build fails if citation coverage < 95% (for RAG paths).",
            "Build fails if policy violations exceed threshold (or any critical class).",
            "Build fails if p95 latency exceeds target by a defined margin.",
            "Build fails if cost/request exceeds budget by a defined margin.",
        ],
        nondeterminism: [
            "Use graded evals (rubrics) instead of exact-match outputs.",
            "Use replay harness with pinned prompts/tools/artifacts.",
            "Track distributions (median/p95), not single samples.",
        ],
        decisions: [
            {
                decision: "Offline eval suite in CI",
                options: "Manual QA / CI gate / post-deploy only",
                why: "Catches regressions before merge and makes release quality repeatable.",
                tradeoff: "Requires maintaining datasets, runners, and reports.",
            },
            {
                decision: "Canary + rollback triggers",
                options: "Big-bang release / canary / feature flag only",
                why: "Limits blast radius and ties rollout to measured behavior.",
                tradeoff: "Requires operational discipline and clear thresholds.",
            },
            {
                decision: "Telemetry as a first-class signal",
                options: "Logs-only / metrics-only / traces+metrics+logs",
                why: "Makes quality, latency, and cost observable and debuggable.",
                tradeoff: "More instrumentation and data management work.",
            },
            {
                decision: "Versioned prompts + policies",
                options: "UI-edits / ad hoc changes / versioned artifacts",
                why: "Enables auditability and safe rollback across teams.",
                tradeoff: "Adds process, but improves reliability at scale.",
            },
        ],
        failureModes: [
            {
                failure: "Flaky evals due to non-determinism",
                mitigation: "Rubric-based scoring + multiple samples; monitor drift trends over time.",
            },
            {
                failure: "Overfitting to golden sets",
                mitigation: "Adversarial + rotating datasets; mix offline and online feedback signals.",
            },
            {
                failure: "Eval suite is too slow for developer flow",
                mitigation: "Tiered suites (fast smoke vs full); cache artifacts; run expensive evals nightly.",
            },
            {
                failure: "False confidence from metrics without audits",
                mitigation: "Trace-first debugging; link eval runs to releases and production incidents.",
            },
        ],
        evaluation: [
            "Offline eval suites: golden sets + adversarial cases (gated merges).",
            "Online monitoring: quality/cost/latency and policy violations by route/workflow.",
            "Canary rollout with rollback triggers and incident playbooks.",
        ],
        evidence: {
            artifacts: [
                "Visual: eval-gate-report.png — example CI gate report with threshold pass/fail table and top regression callout.",
                "Output: release-decision.md — synthetic release decision record (offline evals + canary + rollback triggers).",
            ],
            visual: {
                src: "/assets/images/proof/eval-gate-report.png",
                alt: "Eval gate report screenshot",
                caption:
                    "Synthetic CI gate report showing threshold failures and actionable regression examples for fast rollback decisions.",
            },
            downloads: [
                { label: "release-decision.md", href: "/assets/artifacts/release-decision.md" },
                { label: "evals-dashboard.svg", href: "/assets/images/proof/evals-dashboard.svg" },
            ],
            sample: {
                title: "release-decision.md (excerpt)",
                language: "markdown",
                content: `## Change summary
- Change type: prompt update
- Component: retrieval prompt template

## Canary plan
- Start: 5% traffic for 2 hours
- Expand: 25% for 4 hours if thresholds remain green

## Rollback triggers
- Policy violations > 0.5% (or any critical class)
- Citation coverage < 95% on RAG paths`,
            },
        },
        ctas: [
            { label: "See Enterprise RAG", to: "/case-studies/enterprise-rag-platform" },
            { label: "See Governed Agents", to: "/case-studies/agent-orchestration-governance" },
        ],
    },
};

const CaseStudy = () => {
    const { slug } = useParams();
    const caseStudy = slug ? CASE_STUDIES[slug] : null;
    const canonical = slug
        ? `https://www.theomefford.com/case-studies/${slug}`
        : "https://www.theomefford.com/case-studies";

    const structuredData = caseStudy
        ? {
              "@context": "https://schema.org",
              "@type": "TechArticle",
              headline: caseStudy.title,
              description: caseStudy.valueStatement,
              url: canonical,
              mainEntityOfPage: {
                  "@type": "WebPage",
                  "@id": canonical,
              },
              author: {
                  "@type": "Person",
                  name: "Theo Mefford",
                  url: "https://www.theomefford.com/",
              },
              publisher: {
                  "@type": "Person",
                  name: "Theo Mefford",
              },
              image: caseStudy.ogImage
                  ? `https://www.theomefford.com${caseStudy.ogImage}`
                  : "https://www.theomefford.com/assets/images/portfolio/headshot.jpg",
              about: Array.isArray(caseStudy.about)
                  ? caseStudy.about.map((name) => ({ "@type": "Thing", name }))
                  : undefined,
          }
        : null;

    if (!caseStudy) {
        return (
            <React.Fragment>
                <PageHelmet
                    pageTitle="Case Study Not Found"
                    description="This case study does not exist."
                    canonical="https://www.theomefford.com/case-studies"
                    robots="noindex,follow"
                />
                <HeaderAbs homeLink="/" logo="symbol-dark" color="color-black" />
                <div className="rn-blog-details pt--120 pb--120 bg_color--1">
                    <div className="container">
                        <h2>Case study not found.</h2>
                        <p>
                            <Link to="/reference-architectures">Back to reference architectures</Link>
                        </p>
                    </div>
                </div>
                <FooterTwo />
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <PageHelmet
                pageTitle={caseStudy.title}
                description={caseStudy.valueStatement}
                canonical={canonical}
                ogImage={caseStudy.ogImage}
                structuredData={structuredData}
            />

            <HeaderAbs homeLink="/" logo="symbol-dark" color="color-black" />

            <div className="rn-page-title-area pt--120 pb--70 bg_color--1">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <CaseStudyHero
                                title={caseStudy.title}
                                valueStatement={caseStudy.valueStatement}
                                tldr={caseStudy.tldr}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="rn-blog-details pt--80 pb--120 bg_color--1">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-10 offset-lg-1">
                            <div className="inner-wrapper">
                                {caseStudy.where ? (
                                    <div className="inner mb--50">
                                        <h3 className="title">Where this wins / where it doesn’t</h3>
                                        <div className="row">
                                            <div className="col-lg-6 col-12">
                                                <h4 className="title">Use this when</h4>
                                                <ul>
                                                    {caseStudy.where.useWhen.map((item) => (
                                                        <li key={item}>{item}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="col-lg-6 col-12 mt_md--30 mt_sm--30">
                                                <h4 className="title">Don’t use this when</h4>
                                                <ul>
                                                    {caseStudy.where.dontUseWhen.map((item) => (
                                                        <li key={item}>{item}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}

                                {caseStudy.diagram ? (
                                    <MermaidDiagram
                                        title={caseStudy.diagram.title}
                                        chart={caseStudy.diagram.mermaid}
                                    />
                                ) : null}

                                {Array.isArray(caseStudy.permissionModel) ? (
                                    <div className="inner mb--50">
                                        <h3 className="title">Permission model</h3>
                                        <ul>
                                            {caseStudy.permissionModel.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}

                                {Array.isArray(caseStudy.toolContracts) ? (
                                    <div className="inner mb--50">
                                        <h3 className="title">Tool contract model</h3>
                                        <ul>
                                            {caseStudy.toolContracts.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}

                                {Array.isArray(caseStudy.approvalMatrix) ? (
                                    <div className="inner mb--50">
                                        <h3 className="title">Approval matrix</h3>
                                        <p className="description">HITL approvals required for:</p>
                                        <ul>
                                            {caseStudy.approvalMatrix.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}

                                <DecisionsTable rows={caseStudy.decisions} />

                                <FailureModes items={caseStudy.failureModes} />

                                <div className="inner mb--50">
                                    <h3 className="title">How we evaluate</h3>
                                    <ul>
                                        {caseStudy.evaluation.map((item) => (
                                            <li key={item}>{item}</li>
                                        ))}
                                    </ul>
                                </div>

                                {Array.isArray(caseStudy.thresholds) ? (
                                    <div className="inner mb--50">
                                        <h3 className="title">Threshold examples</h3>
                                        <ul>
                                            {caseStudy.thresholds.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}

                                {Array.isArray(caseStudy.nondeterminism) ? (
                                    <div className="inner mb--50">
                                        <h3 className="title">Handling non-determinism</h3>
                                        <ul>
                                            {caseStudy.nondeterminism.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ) : null}

                                <CTAButtons items={caseStudy.ctas} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <FooterTwo />
        </React.Fragment>
    );
};

export default CaseStudy;
